import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Loader2 } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

export default function BrandedLogin() {
  const { code } = useParams();
  const navigate = useNavigate();
  const { login, verify2fa } = useAuth();
  const [branding, setBranding] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [twofa, setTwofa] = useState(null);
  const [twofaCode, setTwofaCode] = useState("");

  useEffect(() => {
    let cancelled = false;
    axios.get(`${BACKEND}/api/public/branding/${encodeURIComponent(code)}`)
      .then((r) => { if (!cancelled) setBranding(r.data); })
      .catch(() => { if (!cancelled) setNotFound(true); });
    return () => { cancelled = true; };
  }, [code]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await login(email.trim().toLowerCase(), password);
      if (res?.requires_2fa) {
        setTwofa({
          challenge_id: res.challenge_id,
          phone_hint: res.phone_hint,
          email_hint: res.email_hint,
          sms_sent: res.sms_sent,
          email_sent: res.email_sent,
        });
        const channels = [
          res.sms_sent ? "phone" : null,
          res.email_sent ? "email" : null,
        ].filter(Boolean).join(" + ");
        toast.info(channels
          ? `Verification code sent to your ${channels}`
          : "Verification code generated — check your phone & email");
        setBusy(false);
        return;
      }
      if (res.role === "admin") {
        // Admin landed on a CLIENT-branded login — drop them into the
        // client's workspace (tab nav) so they see the full client
        // surface, not the admin sidebar. Persist + audit.
        sessionStorage.setItem("viewAsClientId", branding.client_id);
        sessionStorage.setItem("viewAsClientName",
          branding.company_name || branding.client_id);
        try {
          const { data: ev } = await axios.post(
            `${BACKEND}/api/admin/impersonation/start`,
            { client_id: branding.client_id,
              client_company: branding.company_name },
            { withCredentials: true });
          sessionStorage.setItem("viewAsEventId", ev.event_id);
        } catch { /* audit failure is non-blocking */ }
        toast.success(`Viewing portal as ${branding.company_name || "client"}`);
        navigate(`/clients/${encodeURIComponent(branding.client_id)}`);
        return;
      }
      if (res.role === "client" && res.client_id !== branding?.client_id) {
        toast.error("This login isn't authorised for this client portal.");
        setBusy(false);
        return;
      }
      navigate("/portal");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid credentials");
    } finally { setBusy(false); }
  };

  const verifyCode = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await verify2fa(twofa.challenge_id, twofaCode);
      if (u.role === "admin") {
        sessionStorage.setItem("viewAsClientId", branding.client_id);
        sessionStorage.setItem("viewAsClientName",
          branding.company_name || branding.client_id);
        try {
          const { data: ev } = await axios.post(
            `${BACKEND}/api/admin/impersonation/start`,
            { client_id: branding.client_id,
              client_company: branding.company_name },
            { withCredentials: true });
          sessionStorage.setItem("viewAsEventId", ev.event_id);
        } catch { /* audit failure is non-blocking */ }
        toast.success(`Viewing portal as ${branding.company_name || "client"}`);
        navigate(`/clients/${encodeURIComponent(branding.client_id)}`);
        return;
      }
      if (u.role === "client" && u.client_id !== branding?.client_id) {
        toast.error("This login isn't authorised for this client portal.");
        setBusy(false);
        return;
      }
      navigate("/portal");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Verification failed");
    } finally { setBusy(false); }
  };

  if (notFound) {
    return (
      <div className="min-h-screen grid place-items-center bg-zinc-50 p-6">
        <Card className="rounded-sm max-w-md w-full">
          <CardContent className="p-8 text-center">
            <h1 className="font-display font-extrabold text-2xl tracking-tight">Page not found</h1>
            <p className="text-sm text-zinc-500 mt-2">
              No client matches the code <code className="font-mono">{code}</code>.
            </p>
            <Link to="/login" className="text-xs label-overline text-[#002FA7] hover:underline mt-4 inline-block">
              Go to standard login →
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!branding) {
    return (
      <div className="min-h-screen grid place-items-center bg-zinc-50">
        <Loader2 className="h-6 w-6 animate-spin text-zinc-400" />
      </div>
    );
  }

  const primary = branding.brand_colors?.primary || "#002FA7";
  const hero = branding.login_hero_image_url ? `${BACKEND}${branding.login_hero_image_url}` : null;
  const logo = branding.logo_url ? `${BACKEND}${branding.logo_url}` : null;

  return (
    <div
      className="min-h-screen relative"
      style={hero ? {
        backgroundImage: `url(${hero})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      } : { backgroundColor: primary }}
      data-testid="branded-login-page"
    >
      {/* One brand-tinted gradient across the whole page — keeps the photo
          visible on the right while staying readable on the left. */}
      <div
        className="absolute inset-0"
        style={{
          backgroundImage: hero
            ? `linear-gradient(105deg, ${primary}E6 0%, ${primary}B3 45%, ${primary}40 75%, ${primary}00 100%)`
            : "none",
        }}
      />

      {/* Single left column carrying the hero text + login card */}
      <div className="relative min-h-screen flex items-center px-6 sm:px-12 md:px-16 py-12">
        <div className="w-full max-w-xl space-y-8">
          {/* Hero text */}
          <div className="text-white">
            <div className="text-[10px] uppercase tracking-[0.3em] opacity-70 mb-3">
              Marketing Reports Portal
            </div>
            <h1 className="font-display font-extrabold text-5xl tracking-tight leading-tight">
              {branding.company_name}
            </h1>
            <p className="text-sm opacity-80 mt-4 max-w-md">
              Sign in to view your live marketing dashboard, monthly PDF reports, and
              chat with our AI strategist.
            </p>
          </div>

          {/* Login card */}
          <Card className="rounded-sm border-zinc-200 w-full max-w-md bg-white shadow-lg" data-testid="branded-login-card">
            <CardContent className="p-8 space-y-6">
              <div>
                {logo ? (
                  <img
                    src={logo}
                    alt={branding.company_name}
                    className="h-14 mb-4 object-contain"
                    data-testid="branded-login-logo"
                  />
                ) : (
                  <div
                    className="h-14 w-14 rounded-sm mb-4 flex items-center justify-center font-display font-extrabold text-2xl text-white"
                    style={{ backgroundColor: primary }}
                  >
                    {branding.company_name.slice(0, 2).toUpperCase()}
                  </div>
                )}
                <div className="label-overline">Welcome</div>
                <h2 className="font-display font-extrabold text-2xl tracking-tight mt-1">
                  {branding.company_name}
                </h2>
                <p className="text-xs text-zinc-500 mt-1">Sign in to your portal</p>
              </div>

              {twofa ? (
                <form onSubmit={verifyCode} className="space-y-4" data-testid="branded-login-2fa-form">
                  <div>
                    <div className="label-overline mb-1">Two-factor verification</div>
                    <p className="text-sm text-zinc-500">
                      We&apos;ve sent a 6-digit code to
                      {twofa.phone_hint && twofa.sms_sent
                        ? <> your phone <strong>{twofa.phone_hint}</strong></>
                        : null}
                      {twofa.phone_hint && twofa.sms_sent && twofa.email_hint && twofa.email_sent ? " and " : ""}
                      {twofa.email_hint && twofa.email_sent
                        ? <> your email <strong>{twofa.email_hint}</strong></>
                        : null}
                      {!(twofa.sms_sent || twofa.email_sent)
                        ? " your phone and email"
                        : null}.
                    </p>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="label-overline">Verification code</Label>
                    <Input
                      type="text" inputMode="numeric" autoFocus maxLength={6}
                      pattern="[0-9]{6}" required
                      value={twofaCode}
                      onChange={(e) => setTwofaCode(e.target.value.replace(/\D/g, ""))}
                      placeholder="123456"
                      className="rounded-sm h-11 text-center font-mono text-lg tracking-[0.4em]"
                      data-testid="branded-login-2fa-input"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={busy || twofaCode.length < 6}
                    className="w-full rounded-sm h-11 text-white"
                    style={{ backgroundColor: primary }}
                    data-testid="branded-login-2fa-verify"
                  >
                    {busy ? "Verifying…" : "Verify & sign in"}
                  </Button>
                  <button type="button"
                    className="text-xs label-overline text-zinc-500 hover:text-zinc-900"
                    onClick={() => { setTwofa(null); setTwofaCode(""); }}
                    data-testid="branded-login-2fa-cancel">
                    ← Back to sign-in
                  </button>
                </form>
              ) : (
                <form onSubmit={submit} className="space-y-4">
                  <div className="space-y-1.5">
                    <Label className="label-overline">Email</Label>
                    <Input
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="rounded-sm h-11"
                      data-testid="branded-login-email"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="label-overline">Password</Label>
                    <Input
                      type="password"
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="rounded-sm h-11"
                      data-testid="branded-login-password"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={busy || !email || !password}
                    className="w-full rounded-sm h-11 text-white"
                    style={{ backgroundColor: primary }}
                    data-testid="branded-login-submit"
                  >
                    {busy ? "Signing in…" : `Sign in to ${branding.company_name}`}
                  </Button>
                </form>
              )}

              <div className="text-center text-[11px] text-zinc-400">
                Need general access? <Link to="/login" className="hover:text-[#002FA7]">Standard sign-in</Link>
              </div>

              <div className="flex items-center justify-center gap-3 text-[11px] text-zinc-400 pt-1 flex-wrap">
                <Link to="/terms" className="hover:text-[#002FA7]" data-testid="branded-terms-link">
                  Terms of Service
                </Link>
                <span>·</span>
                <Link to="/privacy" className="hover:text-[#002FA7]" data-testid="branded-privacy-link">
                  Privacy Policy
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
